import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { FiSave, FiArrowLeft, FiInfo } from 'react-icons/fi';
import { productService } from '../services/apiService';
import LoadingSpinner from '../components/LoadingSpinner';

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    product_name: '',
    category: '',
    sub_category: '',
    unit: 'Each',
    // Dual currency prices
    selling_price_usd: '',
    selling_price_lrd: '',
    cost_price_usd: '',
    cost_price_lrd: '',
    tax_category: 'standard',
    barcode: '',
    reorder_level: '',
    opening_stock: '',
  });

  const { data: productData, isLoading: loadingProduct } = useQuery(
    ['product', id],
    () => productService.getById(id),
    { enabled: isEditing }
  );

  useEffect(() => {
    if (productData?.data) {
      const p = productData.data;
      setFormData({
        product_name: p.product_name || '',
        category: p.category || '',
        sub_category: p.sub_category || '',
        unit: p.unit || 'Each',
        selling_price_usd:
          p.selling_price_usd != null ? String(p.selling_price_usd) : '',
        selling_price_lrd:
          p.selling_price_lrd != null ? String(p.selling_price_lrd) : '',
        cost_price_usd:
          p.cost_price_usd != null ? String(p.cost_price_usd) : '',
        cost_price_lrd:
          p.cost_price_lrd != null ? String(p.cost_price_lrd) : '',
        tax_category: p.tax_category || 'standard',
        barcode: p.barcode || '',
        reorder_level: p.reorder_level?.toString() || '',
        opening_stock: '',
      });
    }
  }, [productData]);

  const createMutation = useMutation(productService.create, {
    onSuccess: () => {
      toast.success('Product created successfully');
      queryClient.invalidateQueries('products');
      navigate('/products');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to create product');
    },
  });

  const updateMutation = useMutation(
    (data) => productService.update(id, data),
    {
      onSuccess: () => {
        toast.success('Product updated successfully');
        queryClient.invalidateQueries('products');
        navigate('/products');
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to update product');
      },
    }
  );

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const usdPrice = formData.selling_price_usd
      ? parseFloat(formData.selling_price_usd)
      : null;
    const lrdPrice = formData.selling_price_lrd
      ? parseFloat(formData.selling_price_lrd)
      : null;

    if (!formData.product_name) {
      toast.error('Product name is required');
      return;
    }

    if (!usdPrice && !lrdPrice) {
      toast.error('Enter at least one selling price (USD or LRD)');
      return;
    }

    const payload = {
      product_name: formData.product_name,
      category: formData.category || null,
      sub_category: formData.sub_category || null,
      unit: formData.unit,
      selling_price_usd: usdPrice,
      selling_price_lrd: lrdPrice,
      cost_price_usd: formData.cost_price_usd
        ? parseFloat(formData.cost_price_usd)
        : null,
      cost_price_lrd: formData.cost_price_lrd
        ? parseFloat(formData.cost_price_lrd)
        : null,
      // Legacy fields (backend keeps them synced)
      selling_price: usdPrice ?? lrdPrice ?? 0,
      cost_price: formData.cost_price_usd
        ? parseFloat(formData.cost_price_usd)
        : 0,
      tax_category: formData.tax_category,
      barcode: formData.barcode || null,
      reorder_level: formData.reorder_level
        ? parseInt(formData.reorder_level)
        : 0,
    };

    if (isEditing) {
      updateMutation.mutate(payload);
    } else {
      if (formData.opening_stock) {
        payload.opening_stock = parseInt(formData.opening_stock);
      }
      createMutation.mutate(payload);
    }
  };

  if (loadingProduct) return <LoadingSpinner message="Loading product..." />;

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button className="icon-btn" onClick={() => navigate('/products')}>
            <FiArrowLeft size={20} />
          </button>
          <div>
            <h1 className="page-title">
              {isEditing ? 'Edit Product' : 'Add New Product'}
            </h1>
            <p className="page-subtitle">
              {isEditing
                ? 'Update product information'
                : 'Enter product details below'}
            </p>
          </div>
        </div>
      </div>

      <div className="card" style={{ maxWidth: '900px' }}>
        <form onSubmit={handleSubmit}>
          {/* ---------- Basic Info ---------- */}
          <div className="form-group">
            <label className="form-label">Product Name *</label>
            <input
              type="text"
              name="product_name"
              className="form-input"
              value={formData.product_name}
              onChange={handleChange}
              placeholder="e.g., Coca-Cola 500ml"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Category</label>
              <input
                type="text"
                name="category"
                className="form-input"
                value={formData.category}
                onChange={handleChange}
                placeholder="e.g., Beverages"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Sub Category</label>
              <input
                type="text"
                name="sub_category"
                className="form-input"
                value={formData.sub_category}
                onChange={handleChange}
                placeholder="e.g., Soft Drinks"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Unit</label>
              <select
                name="unit"
                className="form-select"
                value={formData.unit}
                onChange={handleChange}
              >
                <option value="Each">Each</option>
                <option value="Bottle">Bottle</option>
                <option value="Kg">Kg</option>
                <option value="Box">Box</option>
                <option value="Piece">Piece</option>
                <option value="Liter">Liter</option>
                <option value="Carton">Carton</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Barcode</label>
              <input
                type="text"
                name="barcode"
                className="form-input"
                value={formData.barcode}
                onChange={handleChange}
                placeholder="Optional"
              />
            </div>
          </div>

          {/* ---------- Pricing (Dual Currency) ---------- */}
          <div
            style={{
              background: '#f3f4f6',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
              }}
            >
              <FiInfo size={18} color="#1a73e8" />
              <span
                style={{
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#1f2937',
                }}
              >
                Pricing — Enter USD and/or LRD (both optional, at least one
                selling price required)
              </span>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Selling Price (USD $)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="selling_price_usd"
                  className="form-input"
                  value={formData.selling_price_usd}
                  onChange={handleChange}
                  placeholder="e.g., 1.00"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Selling Price (LRD L$)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="selling_price_lrd"
                  className="form-input"
                  value={formData.selling_price_lrd}
                  onChange={handleChange}
                  placeholder="e.g., 185.00"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Cost Price (USD $)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="cost_price_usd"
                  className="form-input"
                  value={formData.cost_price_usd}
                  onChange={handleChange}
                  placeholder="e.g., 0.65"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Cost Price (LRD L$)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="cost_price_lrd"
                  className="form-input"
                  value={formData.cost_price_lrd}
                  onChange={handleChange}
                  placeholder="e.g., 120.00"
                />
              </div>
            </div>
          </div>

          {/* ---------- Tax & Stock ---------- */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Tax Category</label>
              <select
                name="tax_category"
                className="form-select"
                value={formData.tax_category}
                onChange={handleChange}
              >
                <option value="standard">Standard (GST)</option>
                <option value="zero_rated">Zero Rated</option>
                <option value="exempt">Exempt</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Reorder Level</label>
              <input
                type="number"
                name="reorder_level"
                className="form-input"
                value={formData.reorder_level}
                onChange={handleChange}
                placeholder="0"
              />
            </div>
          </div>

          {!isEditing && (
            <div className="form-group">
              <label className="form-label">Opening Stock</label>
              <input
                type="number"
                name="opening_stock"
                className="form-input"
                value={formData.opening_stock}
                onChange={handleChange}
                placeholder="0"
              />
              <small style={{ color: '#6b7280', fontSize: '12px' }}>
                Initial stock quantity (optional)
              </small>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/products')}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              <FiSave size={18} />
              {isSubmitting
                ? 'Saving...'
                : isEditing
                ? 'Update Product'
                : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
